import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import useWebSocket from 'react-use-websocket';
import { CircularProgress, Stack, useTheme } from '@mui/material';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

import { useChat } from '@hooks/useChat';
import { useLanguageStore } from '@store/languageStore';
import { useLocationStore } from '@store/locationStore';
import { SharedApiUrl } from '@services/endpoints';

import AnimatedSphere from './components/AnimatedSphere';
import AvatarHeader from './components/AvatarHeader';
import ErrorMessages from './components/ErrorMessages';
import HideAvatarButton from './components/HideAvatarButton';
import MicrophoneButton from './components/MicrophoneButton';
import StopSpeakingButton from './components/StopSpeakingButton';
import { useCombinedAvatarErrors } from './hooks/useCombinedAvatarErrors';
import { useGetAvatarConnectionData } from './services/queries';
import { RawWebsocketMessageResponse } from './services/types';
import { audioConfig, errorMessages } from './constants';
interface AvatarProps {
    setIsAvatarVisible: Dispatch<SetStateAction<boolean>>;
}

const Avatar = ({ setIsAvatarVisible }: AvatarProps) => {
    const theme = useTheme();
    const locationItem = useLocationStore((state) => state.location);
    const locationValueRef = useRef(locationItem?.value);
    const [isStopSpeakingRequested, setIsStopSpeakingRequested] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [isHandlingMicConnection, setIsHandlingMicConnection] = useState(false);
    const recognizerRef = useRef<SpeechSDK.SpeechRecognizer | null>(null);
    const synthesizerRef = useRef<SpeechSDK.SpeechSynthesizer | null>(null);
    const speechConfigRef = useRef<SpeechSDK.SpeechConfig | null>(null);
    const playerRef = useRef<SpeechSDK.SpeakerAudioDestination | null>(null);
    const [currentCorrelationId, setCurrentCorrelationId] = useState<string | null>(null);
    //second connection
    const analyserRef = useRef<AnalyserNode | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);

    const setIsRecognizingLang = useLanguageStore((state) => state.setIsRecognizingLang);
    const setLanguage = useLanguageStore((state) => state.setLanguage);

    useEffect(() => {
        locationValueRef.current = locationItem?.value;
    }, [locationItem?.value]);

    const {
        data: {
            avatarToken: {
                isPublicNetworkAccessEnabled,
                token,
                hostUrl: speechServiceHostUrl,
                sttEndpoint,
            } = {},
            websocketUrl: { url: urlWithToken } = {},
            avatarSupportedLocales,
        } = {},
        isLoading,
        error: connectionDataError,
        isWebsocketUrlFetching,
    } = useGetAvatarConnectionData();

    const {
        sendMessage,
        stopStreaming,
        error: chatError,
    } = useChat(SharedApiUrl.ChatAvatarGeneral);

    const {
        errors,
        hasErrors,
        setMicError,
        setParseError,
        setWsError,
        setSpeechError,
        setAudioInitError,
    } = useCombinedAvatarErrors({
        connectionDataError,
        chatError,
    });

    useWebSocket(
        urlWithToken ?? null,
        {
            shouldReconnect: () => true,
            onOpen: () => {
                setWsError(null);
            },
            onMessage: (event: MessageEvent<string>) => {
                if (isStopSpeakingRequested) {
                    return;
                }

                try {
                    setParseError(null);
                    const data: RawWebsocketMessageResponse = JSON.parse(event.data);
                    const { correlationId: newCorrelationId, rawText, ssml } = data;

                    const textToSpeak = ssml || rawText;

                    if (!textToSpeak) {
                        return;
                    }

                    if (currentCorrelationId !== newCorrelationId) {
                        playerRef.current?.pause();
                        synthesizerRef.current?.close();
                        synthesizerRef.current = null;
                        setCurrentCorrelationId(newCorrelationId);
                    }

                    if (!synthesizerRef.current && speechConfigRef.current && playerRef.current) {
                        const audioConfig = SpeechSDK.AudioConfig.fromSpeakerOutput(
                            playerRef.current
                        );
                        synthesizerRef.current = new SpeechSDK.SpeechSynthesizer(
                            speechConfigRef.current,
                            audioConfig
                        );
                    }

                    if (synthesizerRef.current && playerRef.current) {
                        playerRef.current.resume();
                        if (ssml) {
                            synthesizerRef.current.speakSsmlAsync(
                                ssml,
                                () => setSpeechError(null),
                                (error) => setSpeechError(new Error(error))
                            );
                        } else {
                            synthesizerRef.current.speakTextAsync(
                                rawText,
                                () => setSpeechError(null),
                                (error) => setSpeechError(new Error(error))
                            );
                        }
                    }
                } catch (error) {
                    if (error instanceof Error) {
                        setParseError(error);
                    } else {
                        setParseError(new Error(String(error)));
                    }
                }
            },
            onError: (error) => {
                let message = errorMessages.websocketGeneral;

                if (error instanceof ErrorEvent) {
                    message = error.message;
                } else if (error instanceof CloseEvent) {
                    message = `${errorMessages.websocketClosed}. ${error.reason}`;
                }
                setWsError(new Error(message));
            },
        },
        !!urlWithToken && !isWebsocketUrlFetching
    );

    const hasSupportedLocales = avatarSupportedLocales && avatarSupportedLocales.length > 0;

    useEffect(() => {
        if (!token || !hasSupportedLocales || !speechServiceHostUrl || !sttEndpoint) return;

        const speechSttConfig = SpeechSDK.SpeechConfig.fromEndpoint(
            new URL(isPublicNetworkAccessEnabled ? speechServiceHostUrl : sttEndpoint)
        );
        let speechTtsConfig;

        if (isPublicNetworkAccessEnabled) {
            speechTtsConfig = SpeechSDK.SpeechConfig.fromEndpoint(new URL(speechServiceHostUrl));
        } else {
            speechTtsConfig = SpeechSDK.SpeechConfig.fromHost(new URL(speechServiceHostUrl));
        }

        speechSttConfig.authorizationToken = token;
        speechTtsConfig.authorizationToken = token;

        speechConfigRef.current = speechTtsConfig;

        playerRef.current = new SpeechSDK.SpeakerAudioDestination();

        const autoDetectLangConfig =
            SpeechSDK.AutoDetectSourceLanguageConfig.fromLanguages(avatarSupportedLocales);

        const audioConfigRecognizer = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();

        const recognizer = SpeechSDK.SpeechRecognizer.FromConfig(
            speechSttConfig,
            autoDetectLangConfig,
            audioConfigRecognizer
        );
        recognizerRef.current = recognizer;

        recognizer.sessionStarted = () => {
            setIsListening(true);
            setIsRecognizingLang(true);
        };

        recognizer.recognized = (_, { result }) => {
            const { reason, text } = result;

            if (reason === SpeechSDK.ResultReason.RecognizedSpeech && text.trim()) {
                const detectedLang =
                    SpeechSDK.AutoDetectSourceLanguageResult.fromResult(result)?.language;

                setLanguage(detectedLang);
                setIsRecognizingLang(false);
                setIsStopSpeakingRequested(false);
                sendMessage(text, locationValueRef.current, detectedLang);
            }
        };

        recognizer.canceled = () => {
            setIsRecognizingLang(false);
            setIsListening(false);
        };

        recognizer.sessionStopped = () => {
            setIsRecognizingLang(false);
            setIsListening(false);
        };

        return () => {
            setIsRecognizingLang(false);
            if (recognizerRef.current) {
                recognizerRef.current.stopContinuousRecognitionAsync();

                recognizerRef.current.recognized = () => {};
                recognizerRef.current.canceled = () => {};
                recognizerRef.current.sessionStopped = () => {};

                recognizerRef.current.close();
                recognizerRef.current = null;
            }

            if (synthesizerRef.current) {
                synthesizerRef.current.close();
                synthesizerRef.current = null;
            }
            if (playerRef.current) {
                playerRef.current.pause();
                playerRef.current.close();
                playerRef.current = null;
            }
        };
    }, [
        token,
        speechServiceHostUrl,
        sendMessage,
        avatarSupportedLocales,
        hasSupportedLocales,
        setIsRecognizingLang,
        setLanguage,
        isPublicNetworkAccessEnabled,
        sttEndpoint,
    ]);

    useEffect(() => {
        if (isListening) {
            const initAudio = async () => {
                try {
                    setAudioInitError(null);
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    mediaStreamRef.current = stream;

                    const audioContext = new AudioContext();
                    const analyser = audioContext.createAnalyser();
                    analyser.fftSize = audioConfig.fftSize;

                    const source = audioContext.createMediaStreamSource(stream);
                    source.connect(analyser);

                    audioContextRef.current = audioContext;
                    analyserRef.current = analyser;
                } catch (error) {
                    if (error instanceof Error) {
                        setAudioInitError(error);
                    } else {
                        setAudioInitError(new Error(String(error)));
                    }
                }
            };

            initAudio();

            return () => {
                if (mediaStreamRef.current) {
                    mediaStreamRef.current.getTracks().forEach((track) => track.stop());
                }
                if (audioContextRef.current) {
                    audioContextRef.current.close();
                }
                audioContextRef.current = null;
                analyserRef.current = null;
                mediaStreamRef.current = null;
            };
        }
    }, [isListening, setAudioInitError]);

    const hasDataRequiredToUseAvatar =
        token && speechServiceHostUrl && urlWithToken && hasSupportedLocales;
    const isAvatarPossibleToDisplay = hasDataRequiredToUseAvatar && !hasErrors;

    const startListening = () => {
        setMicError(null);
        setIsHandlingMicConnection(true);
        recognizerRef.current?.startContinuousRecognitionAsync(
            () => {
                setIsHandlingMicConnection(false);
            },
            (error) => {
                setIsHandlingMicConnection(false);
                setMicError(new Error(error));
            }
        );
    };

    const stopListening = () => {
        setMicError(null);
        setIsHandlingMicConnection(true);
        recognizerRef.current?.stopContinuousRecognitionAsync(
            () => {
                setIsHandlingMicConnection(false);
            },
            (error) => {
                setIsHandlingMicConnection(false);
                setMicError(new Error(error));
            }
        );
        stopSpeaking();
    };

    const stopSpeaking = () => {
        setIsStopSpeakingRequested(true);
        stopStreaming();
        playerRef.current?.pause();
        synthesizerRef.current = null;
    };

    return (
        <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            gap={2}
            sx={{
                backgroundColor: 'black',
                flex: 3,
                borderTopRightRadius: theme.borderRadius.medium,
                borderBottomRightRadius: theme.borderRadius.medium,
            }}
            component='article'
            position='relative'
        >
            {isLoading && <CircularProgress sx={{ color: theme.palette.customColors.purple }} />}
            {hasErrors && <ErrorMessages errors={errors} />}
            {isAvatarPossibleToDisplay && (
                <>
                    <HideAvatarButton setIsAvatarVisible={setIsAvatarVisible} />
                    <AvatarHeader />
                    <AnimatedSphere isListening={isListening} analyserRef={analyserRef} />
                    <Stack direction='row' gap={8}>
                        <MicrophoneButton
                            isListening={isListening}
                            isHandlingMicConnection={isHandlingMicConnection}
                            stopListening={stopListening}
                            startListening={startListening}
                        />
                        {isListening && <StopSpeakingButton stopSpeaking={stopSpeaking} />}
                    </Stack>
                </>
            )}
        </Stack>
    );
};

export default Avatar;
